import { useEffect, useRef, useState } from 'react';
import {
  fetchExploreSearch, type SearchedDestination, type SearchedPlan,
} from '../services/atlasApi';
import { trackDestinationSearched } from './useAnalytics';

/** Long enough that a search is a question rather than a keystroke, and short
 *  enough not to feel laggy. The server refuses anything shorter anyway. */
export const SEARCH_DEBOUNCE_MS = 250;

/** The debounced unified lookup, shared by every search box (slice 4c-3).
 *
 *  Extracted from `HeroSearch` VERBATIM — behaviour, staleness rule, analytics
 *  and failure path — when the map view grew its own box. Two boxes each
 *  carrying a copy of this effect is how the measurement or the staleness rule
 *  drifts apart: this app already keeps one geocoder, one filter vocabulary
 *  and one credit component for the same reason.
 *
 *  Results carry the query they answer, so a stale result is unrepresentable —
 *  `places` is null while THIS query's answer is unknown, which is distinct
 *  from "none". A failed lookup empties this query's results but still
 *  RESOLVES it, so a caller's waiting Enter falls through instead of hanging.
 *  The §22.6 event fires on what was FOUND, never what was asked.
 */
export function useExploreSearch(query: string, active: boolean) {
  const [found, setFound] = useState<{
    q: string; results: SearchedDestination[]; plans: SearchedPlan[];
  }>({ q: '', results: [], plans: [] });
  const latestQuery = useRef('');

  useEffect(() => {
    latestQuery.current = query;
    if (!active) {
      setFound({ q: query, results: [], plans: [] });
      return;
    }
    const timer = setTimeout(() => {
      fetchExploreSearch(query)
        .then(res => {
          if (latestQuery.current !== query) return;
          setFound({ q: query, results: res.destinations, plans: res.plans });
          if (res.outcome) {
            trackDestinationSearched(
              res.outcome,
              res.query_type ?? 'free_text',
              res.query_len,
              res.resolved_slug ?? null,
            );
          }
        })
        .catch(() => {
          if (latestQuery.current === query) {
            setFound({ q: query, results: [], plans: [] });
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, active]);

  const places: SearchedDestination[] | null =
    found.q === query ? found.results : null;
  const plans: SearchedPlan[] = found.q === query ? found.plans : [];
  return { places, plans };
}
